import React, { useMemo, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import {
  BugherdMentionWithReactView,
  createBugherdLinkExtension,
  createMarkdownPreviewStarterKit,
  createMarkdownPreviewCodeBlock
} from '../extensions/tiptapShared';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { createPreviewableImageExtension } from '../extensions/previewableImage';
import type { CommentImagePreviewStorage } from './CommentImageView';
import { markdownToHtml } from '../utils/serialization';
import { MentionPreviewContext } from './mentionPreviewContext';
import { resolveMentionPreviewContext, type MarkdownMentionPreviewContext } from '../types';
import styles from './MarkdownPreview.module.css';
import proseStyles from '../richTextProse.module.css';

export type MarkdownPreviewProps = {
  content: string;
  htmlContent?: string | null;
  className?: string;
  maxLines?: number;
  mentionPreviewContext?: MarkdownMentionPreviewContext;
  interactive?: boolean;
  imagePreviewContainer?: () => HTMLElement;
};

export const MarkdownPreview = ({
  content,
  htmlContent,
  className,
  maxLines = 0,
  mentionPreviewContext,
  interactive = false,
  imagePreviewContainer
}: MarkdownPreviewProps) => {
  const mentionCtxRef = useRef(mentionPreviewContext);
  mentionCtxRef.current = mentionPreviewContext;
  const imagePreviewContainerRef = useRef(imagePreviewContainer);
  imagePreviewContainerRef.current = imagePreviewContainer;

  const extensions = useMemo(
    () => [
      createMarkdownPreviewStarterKit(),
      createMarkdownPreviewCodeBlock(),
      Underline,
      Highlight,
      imagePreviewContainer ? createPreviewableImageExtension() : Image,
      TaskList,
      TaskItem.configure({ nested: true }),
      createBugherdLinkExtension({ openOnClick: false }),
      BugherdMentionWithReactView
    ],
    [Boolean(imagePreviewContainer)]
  );

  const previewHtml = htmlContent?.trim() ? htmlContent : markdownToHtml(content || '');

  const editor = useEditor({
    extensions,
    content: previewHtml,
    editable: false,
    editorProps: {
      attributes: {
        class: `${styles.mdPreview} ${interactive ? styles.mdPreviewStatic : ''} ${proseStyles.proseRoot}`
      },
      handleDOMEvents: {
        click: (_view, event) => {
          const target = event.target as HTMLElement;
          const readMore = target.closest(
            'a.bugherd-description-read-more-link[data-local-task-id]'
          );
          if (readMore) {
            const ctx = mentionCtxRef.current;
            if (ctx?.setActiveTask) {
              event.preventDefault();
              event.stopPropagation();
              const raw = readMore.getAttribute('data-local-task-id');
              if (raw) ctx.setActiveTask(parseInt(raw, 10));
              return true;
            }
          }
          return false;
        }
      }
    },
    immediatelyRender: false,
    onCreate: ({ editor: createdEditor }) => {
      const imageExtension = createdEditor.extensionManager.extensions.find(
        extension => extension.name === 'image'
      );
      const storage = imageExtension?.storage as CommentImagePreviewStorage | undefined;
      if (!storage) return;
      storage.getContainer = () =>
        imagePreviewContainerRef.current?.() || document.body;
    }
  });

  React.useEffect(() => {
    if (editor) {
      editor.commands.setContent(
        htmlContent?.trim() ? htmlContent : markdownToHtml(content || ''),
        false
      );
    }
  }, [content, htmlContent, editor]);

  const resolvedMentionContext = useMemo(
    () => resolveMentionPreviewContext(mentionPreviewContext),
    [mentionPreviewContext]
  );

  if (!editor) return null;

  return (
    <MentionPreviewContext.Provider value={resolvedMentionContext}>
      <div
        className={`${styles.mdPreviewContainer} ${interactive ? styles.mdPreviewContainerInteractive : ''} ${className || ''}`}
        style={
          maxLines
            ? {
                display: '-webkit-box',
                WebkitLineClamp: maxLines,
                WebkitBoxOrient: 'vertical' as const
              }
            : undefined
        }
      >
        <EditorContent editor={editor} />
      </div>
    </MentionPreviewContext.Provider>
  );
};
